/**
 * Shrinks an image in the browser before it is uploaded.
 *
 * Garment photos come off phones at 3000-5000px and several megabytes. Every
 * one of them is base64-encoded into *every* render request — three references
 * on a twelve-frame shoot means the same pixels are uploaded thirty-six times.
 * Renderers work from roughly 1-2k on the long edge, so the extra resolution
 * buys nothing and costs upload time, request size and latency on every frame.
 *
 * Falls back to the original file whenever anything is unsupported, so a
 * failure here can never block an upload.
 */

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.92;

export async function downscaleImage(file: File, maxEdge = MAX_EDGE): Promise<File> {
  if (!file.type.startsWith('image/')) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    if (Math.max(width, height) <= maxEdge) {
      bitmap.close();
      return file;
    }

    const scale = maxEdge / Math.max(width, height);
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close();

    // PNG keeps hard edges on cutouts crisp; photographs are far smaller as JPEG.
    const keepPng = file.type === 'image/png';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, keepPng ? 'image/png' : 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;

    const name = keepPng ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: blob.type, lastModified: Date.now() });
  } catch {
    return file;
  }
}
